// Enhanced Button Component
// US-017: Design System Consistency Implementation

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

// Loading spinner component
const Spinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// Button variants using class-variance-authority for type-safe styling
const buttonVariants = cva(
  [
    // Base button styles
    'inline-flex items-center justify-center gap-2',
    'font-medium transition-all duration-200 ease-out',
    'cursor-pointer select-none',
    'border outline-none',
    'relative',
    
    // Focus styles
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    
    // Disabled styles
    'disabled:cursor-not-allowed disabled:opacity-60 disabled:pointer-events-none disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
    
    // Prevent text selection
    'user-select-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-primary-500 text-white border-primary-500',
          'hover:bg-primary-600 hover:border-primary-600 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-primary-700 active:border-primary-700 active:shadow-sm',
          'focus-visible:outline-primary-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        secondary: [
          'bg-slate-50 text-slate-700 border-slate-300',
          'hover:bg-slate-100 hover:border-slate-400 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-slate-200 active:border-slate-500 active:shadow-sm',
          'focus-visible:outline-primary-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        ghost: [
          'bg-transparent text-slate-700 border-transparent',
          'hover:bg-slate-100 hover:text-slate-900',
          'active:bg-slate-200',
          'focus-visible:outline-primary-500',
          'disabled:bg-transparent disabled:text-slate-400',
        ],
        destructive: [
          'bg-red-600 text-white border-red-600',
          'hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-red-800 active:border-red-800 active:shadow-sm',
          'focus-visible:outline-red-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        outline: [
          'bg-transparent text-primary-500 border-primary-500',
          'hover:bg-primary-50 hover:text-primary-600 hover:border-primary-600',
          'active:bg-primary-100 active:text-primary-700 active:border-primary-700',
          'focus-visible:outline-primary-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        success: [
          'bg-success-500 text-white border-success-500',
          'hover:bg-success-600 hover:border-success-600 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-success-700 active:border-success-700 active:shadow-sm',
          'focus-visible:outline-success-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        /** Cosellus Entity Manager / emerald actions — avoids blue `primary` token */
        emerald: [
          'bg-emerald-600 text-white border-emerald-600',
          'hover:bg-emerald-700 hover:border-emerald-700 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-emerald-800 active:border-emerald-800 active:shadow-sm',
          'focus-visible:outline-emerald-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        warning: [
          'bg-warning-500 text-white border-warning-500',
          'hover:bg-warning-600 hover:border-warning-600 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-warning-700 active:border-warning-700 active:shadow-sm',
          'focus-visible:outline-warning-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        agent: [
          'bg-gradient-to-r from-primary-500 to-[#39b500] text-white border-transparent',
          'hover:from-primary-600 hover:to-[#2d8a00] hover:-translate-y-0.5 hover:shadow-lg',
          'active:translate-y-0 active:from-primary-700 active:to-[#267300] active:shadow-md',
          'focus-visible:outline-primary-500',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        emma: [
          'bg-[#004a77] text-white border-[#004a77]',
          'hover:bg-[#003d63] hover:border-[#003d63] hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-[#003355] active:border-[#003355] active:shadow-sm',
          'focus-visible:outline-[#004a77]',
          'disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-300',
        ],
        sofia: [
          'bg-green-600 text-white border-green-600',
          'hover:bg-green-700 hover:border-green-700 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-green-800 active:border-green-800 active:shadow-sm',
          'focus-visible:outline-green-500',
          'disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300',
        ],
        casey: [
          'bg-amber-600 text-white border-amber-600',
          'hover:bg-amber-700 hover:border-amber-700 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-amber-800 active:border-amber-800 active:shadow-sm',
          'focus-visible:outline-amber-500',
          'disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300',
        ],
        zara: [
          'bg-red-600 text-white border-red-600',
          'hover:bg-red-700 hover:border-red-700 hover:-translate-y-0.5 hover:shadow-md',
          'active:translate-y-0 active:bg-red-800 active:border-red-800 active:shadow-sm',
          'focus-visible:outline-red-500',
          'disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300',
        ],
        thinking: [
          'bg-gray-100 text-gray-600 border-gray-200',
          'animate-pulse',
          'hover:bg-gray-200 hover:border-gray-300',
          'disabled:opacity-100',
        ],
        processing: [
          'bg-blue-100 text-blue-800 border-blue-200',
          'animate-pulse',
          'hover:bg-blue-200 hover:border-blue-300',
          'disabled:opacity-100',
        ],
        complete: [
          'bg-green-100 text-green-800 border-green-200',
          'hover:bg-green-200 hover:border-green-300',
          'disabled:opacity-100',
        ],
      },
      size: {
        xs: [
          'h-6 px-2 text-xs',
          'rounded-sm',
        ],
        sm: [
          'h-8 px-3 text-sm',
          'rounded-md',
        ],
        md: [
          'h-10 px-4 text-sm',
          'rounded-md',
        ],
        lg: [
          'h-11 px-6 text-base',
          'rounded-lg',
        ],
        xl: [
          'h-12 px-8 text-lg',
          'rounded-lg',
        ],
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

// Button props interface
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Icon to display on the left side
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to display on the right side
   */
  rightIcon?: React.ReactNode;
  /**
   * Accessible label for loading state
   */
  loadingText?: string;
  /**
   * Full width button
   */
  fullWidth?: boolean;
  /**
   * Children content
   */
  children: React.ReactNode;
}

/**
 * Enhanced Button component with comprehensive design system integration
 * 
 * Features:
 * - Multiple variants (primary, secondary, ghost, destructive, outline, success, warning, agent)
 * - Size variants (xs, sm, md, lg, xl)
 * - Loading states with spinner
 * - Left and right icon support
 * - Full width option
 * - Accessibility compliant
 * - Dark theme support
 * - Responsive design
 * - TypeScript support
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md" loading={isLoading}>
 *   Save Changes
 * </Button>
 * 
 * <Button variant="outline" leftIcon={<PlusIcon />}>
 *   Add Item
 * </Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  disabled,
  children,
  className,
  loadingText,
  ...props
}, ref) => {
  const buttonClasses = cn(buttonVariants({ variant, size, fullWidth }), className);

  return (
    <button
      ref={ref}
      className={buttonClasses}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-label={loading && loadingText ? loadingText : undefined}
      {...props}
    >
      {/* Loading spinner */}
      {loading && (
        <Spinner className="h-4 w-4" />
      )}
      
      {/* Left icon */}
      {!loading && leftIcon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {leftIcon}
        </span>
      )}
      
      {/* Button content */}
      {loading ? (
        <span className={cn({
          'sr-only': loadingText,
          'opacity-0': !loadingText,
        })}>
          {children}
        </span>
      ) : (
        children
      )}
      
      {/* Loading text */}
      {loading && loadingText && (
        <span>
          {loadingText}
        </span>
      )}
      
      {/* Right icon */}
      {!loading && rightIcon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export { Button, buttonVariants };
export type { VariantProps };
